import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useForm } from 'react-hook-form';
import { connect } from 'react-redux';
import { postAPI, reinitialiseAPI } from 'store/apiReducer';
import { Button, Col, Container, Link, LoaderMi, Row } from '@si-armes/dsfr-react';
import { ReactComponent as Licencie } from 'assets/compte/icone_licencie.svg';
import { ReactComponent as Chasseur } from 'assets/compte/icone_chasseur.svg';
import { ReactComponent as Collectionneur } from 'assets/compte/icone_collectionneur.svg';
import { ReactComponent as NonLicencie } from 'assets/compte/icone_non_licencie.svg';
import { ReactComponent as MetierEtAssociation } from 'assets/compte/icone_metier_association.svg';
import usagerClientReducer from 'store/usagerReducer';
import {
    API_COMPTE_CREER_MOTIF_URL,
    CONTEXT_COMPTE_CREER_MOTIF,
} from 'constants/apiUrls.constants';
import {
    FEATURE_CREER_COMPTE_DETENTEUR_LICENCIEE,
    FEATURE_RENOMMAGE_PERSONA_DETENTEUR_HERITEE_TROUVEE,
    FEATURE_METIERS_ET_ASSOCIATIONS,
    FEATURE_COLLECTIONNEURS,
    FEATURE_MINEURS,
    isFeatureActive,
} from 'components/FeatureFlipping/featureFlipping.func';
import ListeMotif from './ListeMotif';
import styles from './ListeMotif.module.scss';

const CreerCompteChoixMotif = ({
    motifsSelectionnes,
    setMotifsSelectionnes,
    postApi,
    isLoading,
    erreur,
    message,
    data,
    reinitialiseApi,
    featureFlipping,
}) => {
    const [motifsCoches, setMotifsCoches] = useState();

    const { register, handleSubmit, watch, getValues, setValue } = useForm({
        mode: 'onChange',
        defaultValues: {
            motif: {
                ...motifsSelectionnes,
                isChasseur: false,
                isLicencie: false,
                isNonLicencie: false,
                isDetenteurProfessionnel: false,
                isMineur: false,
            },
        },
    });

    const motifsPersonnel = [
        {
            id: 'isChasseur',
            nom: 'Chasseur',
            iconeComponent: Chasseur,
            disable: ['isLicencie', 'isNonLicencie', 'isDetenteurProfessionnel'],
        },
        {
            id: 'isNonLicencie',
            nomPlusieursLignes: isFeatureActive(
                featureFlipping,
                FEATURE_RENOMMAGE_PERSONA_DETENTEUR_HERITEE_TROUVEE,
            )
                ? ['Détenteurs sans titre', '(armes héritées, C3, C9)']
                : ["Détenteur d'armes", 'héritées ou trouvées'],
            iconeComponent: NonLicencie,
            disable: ['isChasseur', 'isLicencie', 'isDetenteurProfessionnel'],
        },
        {
            id: 'isLicencie',
            nomPlusieursLignes: ['Licenciés', '(ou Anciens licenciés)'],
            iconeComponent: Licencie,
            disable: [
                'isChasseur', // retirer 'isChasseur' lors de l'ouverture du service aux autres motifs
                'isNonLicencie',
                'isDetenteurProfessionnel',
            ],
            inactif:
                featureFlipping &&
                !isFeatureActive(featureFlipping, FEATURE_CREER_COMPTE_DETENTEUR_LICENCIEE),
        },
        ...(featureFlipping && !isFeatureActive(featureFlipping, FEATURE_COLLECTIONNEURS)
            ? [
                  {
                      id: 'isCollectionneur',
                      nom: 'Collectionneur',
                      iconeComponent: Collectionneur,
                      inactif: true,
                  },
              ]
            : []),
    ];

    const motifsProfessionnel = [
        {
            id: 'isDetenteurProfessionnel',
            nom: isFeatureActive(featureFlipping, FEATURE_COLLECTIONNEURS)
                ? undefined
                : 'Métier et Association',
            nomPlusieursLignes: isFeatureActive(featureFlipping, FEATURE_COLLECTIONNEURS)
                ? ['Métiers, Associations', 'et Collectionneurs']
                : undefined,
            iconeComponent: MetierEtAssociation,
            inactif:
                featureFlipping &&
                !isFeatureActive(featureFlipping, FEATURE_METIERS_ET_ASSOCIATIONS),
            disable: ['isChasseur', 'isLicencie', 'isNonLicencie'],
        },
    ];

    const onSubmit = (dataForm) => {
        if (
            dataForm &&
            dataForm.motif &&
            Object.values(dataForm.motif).find((val) => val === true)
        ) {
            setMotifsCoches(dataForm.motif);
            postApi(
                usagerClientReducer,
                API_COMPTE_CREER_MOTIF_URL,
                dataForm.motif,
                CONTEXT_COMPTE_CREER_MOTIF,
            );
        }
    };

    // on mount et unmount
    useEffect(() => {
        reinitialiseApi(CONTEXT_COMPTE_CREER_MOTIF);
        return () => {
            reinitialiseApi(CONTEXT_COMPTE_CREER_MOTIF);
        };
    }, []);

    useEffect(() => {
        if (isLoading === false && data && !erreur) {
            setMotifsSelectionnes({ ...motifsCoches, uuidBrouillon: data.uuidBrouillon });
        }
    }, [isLoading, data, erreur]);

    const hasFormValid = () => {
        return (
            !getValues('motif.isChasseur') &&
            !getValues('motif.isLicencie') &&
            !getValues('motif.isNonLicencie') &&
            !getValues('motif.isDetenteurProfessionnel') &&
            !getValues('motif.isMineur')
        );
    };

    const detailCompteNonLicencie = () => {
        if (!getValues('motif.isNonLicencie')) {
            return null;
        }

        return (
            <Container>
                {isFeatureActive(
                    featureFlipping,
                    FEATURE_RENOMMAGE_PERSONA_DETENTEUR_HERITEE_TROUVEE,
                ) ? (
                    <>
                        <p>
                            Un compte « détenteurs sans titre » doit être créé dans les situations
                            suivantes :
                        </p>
                        <ul>
                            <li>Armes héritées ou trouvées</li>
                            <li>
                                Acquisition d&apos;une arme C3 ou C9 (armes de défenses ou armes
                                neutralisées).
                            </li>
                        </ul>
                        <p>
                            La création d&apos;un compte spécifique "Détenteurs sans titre (armes
                            héritées, C3, C9)" ne concerne donc ni les titulaires d&apos;un permis
                            de chasser ou d&apos;une carte de collectionneur, ni les licenciés ou
                            anciens licenciés des fédérations de tir, de ball-trap ou de ski
                            (biathlon).
                        </p>
                    </>
                ) : (
                    <>
                        <p>
                            Un compte spécifique &quot;Détenteur d&apos;armes héritées ou
                            trouvées&quot; est créé dans le SIA par toute personne qui souhaite
                            conserver une telle arme mais qui n&apos;est pas en mesure, à
                            l&apos;instant où elle entre en sa possession, de l&apos;acquérir ou de
                            les détenir légalement.
                        </p>
                        <p>
                            La création d&apos;un compte spécifique &quot;Détenteur d&apos;armes
                            héritées ou trouvées&quot; ne concerne donc ni les titulaires d&apos;un
                            permis de chasser ou d&apos;une carte de collectionneur, ni les
                            licenciés ou anciens licenciés des fédérations de tir, de ball-trap ou
                            de ski (biathlon).
                        </p>
                        <p>
                            Ce compte &quot;Détenteur d&apos;armes héritées ou trouvées&quot;
                            concerne uniquement les personnes physiques n&apos;ayant
                            jusqu&apos;alors aucune capacité juridique à détenir légalement une
                            arme. Celles-ci vont pouvoir enregistrer leurs armes héritées ou
                            trouvées dans le râtelier numérique de leur compte et ainsi les détenir
                            légalement.
                        </p>
                    </>
                )}
                <p>
                    Pour plus d&apos;informations, contactez la préfecture de votre département ou{' '}
                    <Link
                        href="https://www.service-public.fr/particuliers/vosdroits/F11629"
                        icon="fr-fi-arrow-right-line"
                        target="_blank"
                        aria-label="rendez-vous sur service-public.fr, dans un nouvel onglet"
                    >
                        rendez-vous sur service-public.fr
                    </Link>
                </p>
            </Container>
        );
    };

    return (
        <>
            {!featureFlipping && <LoaderMi taille="lg" />}
            <Container>
                <h1 data-testid="account-creation-step0-title">Création de compte</h1>
                <Row>
                    <Col col={12}>
                        <p className={`${styles['text-style-h5']}`} role="doc-subtitle">
                            Veuillez sélectionner le profil de votre compte :
                        </p>
                    </Col>
                </Row>
                <Row>
                    <Col col={12}>
                        Pour la création &quot;métier/association&quot;, vous devez créer un compte
                        distinct
                    </Col>
                </Row>
                {erreur && message && (
                    <Row>
                        <Col col={12}>
                            <p className="fr-error">{message}</p>
                        </Col>
                    </Row>
                )}
            </Container>
            <form onSubmit={handleSubmit(onSubmit)}>
                <div className="creation-compte">
                    <Container>
                        <Row gutters>
                            <Col col={12} lg={8}>
                                <div className="fr-form-group" style={{ padding: '2rem 0' }}>
                                    <fieldset className="fr-fieldset">
                                        <legend
                                            className="fr-fieldset__legend type-compte"
                                            id="radio-hint-legend-actif"
                                        >
                                            Compte personnel
                                            <span className="fr-hint-text texte">
                                                Compte dédié aux particuliers demandeurs et
                                                détenteurs d&apos;armes à titre personnel
                                            </span>
                                        </legend>
                                        <div className="fr-fieldset__content">
                                            <ListeMotif
                                                register={register}
                                                motifs={motifsPersonnel}
                                                motifsSelectionnes={watch('motif') ?? {}}
                                            />
                                        </div>
                                    </fieldset>
                                </div>
                                {isFeatureActive(featureFlipping, FEATURE_MINEURS) && (
                                    <Container>
                                        <Row>
                                            <Col col={12} className="text-center">
                                                <Button
                                                    style={{
                                                        backgroundColor: 'white',
                                                        marginBottom: '1rem',
                                                    }}
                                                    variant="secondary"
                                                    type="button"
                                                    icon="fr-fi-account-fill"
                                                    iconPosition="left"
                                                    onClick={() => {
                                                        setValue('motif.isChasseur', false);
                                                        setValue('motif.isLicencie', false);
                                                        setValue('motif.isNonLicencie', false);
                                                        setValue(
                                                            'motif.isDetenteurProfessionnel',
                                                            false,
                                                        );
                                                        setValue('motif.isMineur', true);
                                                        const dataForm = {
                                                            motif: {
                                                                isChasseur: false,
                                                                isLicencie: false,
                                                                isNonLicencie: false,
                                                                isDetenteurProfessionnel: false,
                                                                isMineur: true,
                                                            },
                                                        };
                                                        setMotifsCoches(dataForm.motif);
                                                        postApi(
                                                            usagerClientReducer,
                                                            API_COMPTE_CREER_MOTIF_URL,
                                                            dataForm.motif,
                                                            CONTEXT_COMPTE_CREER_MOTIF,
                                                        );
                                                    }}
                                                >
                                                    Compte détenteur mineur
                                                </Button>
                                            </Col>
                                        </Row>
                                    </Container>
                                )}
                            </Col>
                            <Col col={12} lg={4}>
                                <div className="fr-form-group" style={{ padding: '2rem 0' }}>
                                    <fieldset className="fr-fieldset">
                                        <legend
                                            className="fr-fieldset__legend type-compte"
                                            id="radio-hint-legend-inactif"
                                        >
                                            Autre Compte
                                            <span className="fr-hint-text texte">
                                                Compte dédié aux détenteurs du monde associatif et
                                                aux personnes dont l&apos;activité (autre
                                                qu&apos;armurier) nécessite la détention
                                                d&apos;armes
                                            </span>
                                        </legend>
                                        <div className="fr-fieldset__content">
                                            <ListeMotif
                                                register={register}
                                                motifs={motifsProfessionnel}
                                                motifsSelectionnes={watch('motif') ?? {}}
                                            />
                                        </div>
                                    </fieldset>
                                </div>
                            </Col>
                        </Row>
                    </Container>
                </div>
                {detailCompteNonLicencie()}
                <Row>
                    <Col className="text-center">
                        <Button
                            variant="primary"
                            type="submit"
                            icon="fr-fi-account-fill"
                            iconPosition="left"
                            disabled={isLoading || hasFormValid()}
                        >
                            {isLoading ? 'Chargement' : 'Étape suivante, identité'}
                        </Button>
                    </Col>
                </Row>
            </form>
        </>
    );
};

CreerCompteChoixMotif.propTypes = {
    motifsSelectionnes: PropTypes.shape({
        isChasseur: PropTypes.bool,
        isLicencie: PropTypes.bool,
        isNonLicencie: PropTypes.bool,
        isDetenteurProfessionnel: PropTypes.bool,
        isMineur: PropTypes.bool,
    }),
    setMotifsSelectionnes: PropTypes.func.isRequired,
    postApi: PropTypes.func.isRequired,
    reinitialiseApi: PropTypes.func.isRequired,
    isLoading: PropTypes.bool,
    erreur: PropTypes.bool,
    data: PropTypes.shape({
        uuidBrouillon: PropTypes.string.isRequired,
    }),
    message: PropTypes.string,
    featureFlipping: PropTypes.object.isRequired,
};

export default connect(
    (state) => {
        const { isLoading, erreur, data, message } = state.compte.creer.choisirMotif;
        return {
            isLoading,
            erreur,
            data,
            message,
        };
    },
    {
        postApi: postAPI,
        reinitialiseApi: reinitialiseAPI,
    },
)(CreerCompteChoixMotif);
